import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import cn from 'classnames';
import copy from 'copy-to-clipboard';
import { Link } from 'react-router-dom';
import { loader } from 'graphql.macro';
import { useMutation, useQuery } from 'react-apollo-hooks';
import { format } from 'date-fns';

import { WithTitle, Checkbox, Button, Field, Select, Form, Toggle, RouteHint, useNotifications } from '@loyalup/ui';

import onlyData from 'utils/onlyData';
import downloadImage from 'utils/downloadImage';
import store from 'utils/store';

import { ReactComponent as CheckedIcon } from 'icons/checked.svg';

import s from './ClientSignUpForm.module.css';

const COMPANY_QUERY = loader('../../gql/queries/Company.gql');
const UPDATE_CLIENT_SIGNUP_SETTINGS_MUTATION = loader('../../gql/mutations/UpdateClientSingupSettings.gql');

const noop = () => {};

const placeholders = {
  ru: {
    firstName: 'Иван',
    lastName: 'Иванов',
    email: 'ivanov@ivan.ru',
    phone: '+7(961)000-00-00',
    bday: format(new Date(1990, 3, 3), 'y-MM-dd'),
    permitHandleData: true,
  },
  en: {
    firstName: 'John',
    lastName: 'Doe',
    email: 'john@doe.com',
    phone: '+44(000)000-00-00',
    bday: format(new Date(1990, 3, 3), 'y-MM-dd'),
    permitHandleData: true,
  },
};

const ClientSignUpForm = () => {
  const [t, i18n] = useTranslation();
  const [sendNotification] = useNotifications();

  const [showHint, setShowHint] = useState(!store.hasLearned('signup'));

  const { data } = useQuery(COMPANY_QUERY, {
    suspend: false,
    fetchPolicy: 'cache-and-network',
    notifyOnNetworkStatusChange: true,
  });

  const [company, setCompany] = useState(null);

  useEffect(() => {
    if (data) {
      setCompany(data.company);
    }
  }, [data]);

  const [updateClientSignupSettings] = useMutation(UPDATE_CLIENT_SIGNUP_SETTINGS_MUTATION);

  const updateSettings = newSettings => {
    const clientSignupSettings = { ...company.clientSignupSettings, ...newSettings };
    setCompany({ ...company, clientSignupSettings });

    updateClientSignupSettings({
      variables: {
        clientSignupSettings: onlyData(clientSignupSettings),
      },
    });
  };

  const { bdayEnabled, firstNameEnabled, lastNameEnabled, emailEnabled, agreementUrl, termsEnabled } =
    company?.clientSignupSettings || {};

  const form = placeholders[i18n.languages[0]];

  const onCopyToClipboard = data => {
    copy(data);
    sendNotification({ text: t('Скопировано!'), ttl: 1000 });
  };

  const embedCode = `<iframe width="400" height="638" src="${company?.signupUrl}" frameborder="0" allow="accelerometer; autoplay; encrypted-media; gyroscope; picture-in-picture" allowfullscreen></iframe>`;

  return (
    <WithTitle title={t('Анкета для выпуска карты')}>
      <div className={s.header}>
        <div className={s.title}>
          <h1>{t('Анкета для выпуска карты')}</h1>
          <RouteHint
            show={showHint}
            setShow={setShowHint}
            onConfirm={() => store.setLearned('signup')}
            className={s.leftOffset}
          >
            <ol className={s.hintRoot}>
              <li>
                <b className={s.num}>
                  <span>1</span>
                </b>
                {t('Выберите поля, которые должны заполнить ваши клиенты')}
              </li>
              <li>
                <b className={s.num}>
                  <span>2</span>
                </b>
                {t('Нажмите Опубликовать анкету и выберите подходящий способ:')}
                <ul className={s.hintSubList}>
                  <li>{t('HTML-код анкеты для встраивания на сайт')}</li>
                  <li>{t('QR-код анкеты')}</li>
                  <li>{t('Ссылка на анкету на нашем сервере')}</li>
                </ul>
              </li>
            </ol>
          </RouteHint>
        </div>
        <Select
          theme="green"
          label={t('Опубликовать анкету')}
          items={[
            {
              label: t('Встроить на сайт'),
              data: embedCode,
            },
            {
              label: t('QR код на анкету'),
              data: company?.signupQrcode,
              download: `Signup QR code ${company?.name}.png`,
            },
            {
              label: t('Ссылка на анкету'),
              data: company?.signupUrl,
            },
          ]}
          onChange={item => {
            item.download && downloadImage(item.data, item.download);
            onCopyToClipboard(item.data);
          }}
          className={cn({ [s.blur]: showHint })}
        />
      </div>
      {company && (
        <>
          <main className={cn(s.main, { [s.blur]: showHint })}>
            <Form onSubmit={noop} className={s.formPreview}>
              <h2>{t('Пример формы')}</h2>
              {firstNameEnabled && <Field label={t('Имя')} type="text" value={form.firstName} onChange={noop} />}

              {lastNameEnabled && <Field label={t('Фамилия')} type="text" value={form.lastName} onChange={noop} />}

              <Field label={t('Телефон')} type="phone" value={form.phone} onChange={noop} />

              {emailEnabled && <Field label={t('Е-мейл')} type="email" value={form.email} onChange={noop} />}

              {bdayEnabled && <Field label={t('День Рождения')} type="date" value={form.bday} onChange={noop} />}

              {termsEnabled && (
                <div className={s.row}>
                  <Checkbox className={s.item} checked={form.permitHandleData} onChange={noop} />
                  <span>{t('Я даю своё согласие на обработку моих персональных данных')}</span>
                </div>
              )}

              <Button className={s.button}>
                <span>{t('Получить карту')}</span>
              </Button>
            </Form>
            <div className={s.model}>
              <h2>{t('Поля для анкеты')}</h2>

              <Toggle
                className={cn(s.modelRow, { [s.enabled]: firstNameEnabled })}
                label={t('Имя')}
                checked={firstNameEnabled}
                onChange={() => {
                  updateSettings({ firstNameEnabled: !firstNameEnabled });
                }}
              />

              <Toggle
                label={t('Фамилия')}
                className={cn(s.modelRow, { [s.enabled]: lastNameEnabled })}
                checked={lastNameEnabled}
                onChange={() => {
                  updateSettings({ lastNameEnabled: !lastNameEnabled });
                }}
              />

              <Toggle
                label={t('Телефон')}
                hintText={t('Обязательное поле')}
                className={cn(s.modelRow, s.enabled, s.blockedCursor)}
                checked={true}
                onChange={() => true}
                disabled
              />

              <Toggle
                label={t('Е-мейл')}
                className={cn(s.modelRow, { [s.enabled]: emailEnabled })}
                checked={emailEnabled}
                onChange={() => {
                  updateSettings({ emailEnabled: !emailEnabled });
                }}
              />

              <Toggle
                label={t('День Рождения')}
                className={cn(s.modelRow, { [s.enabled]: bdayEnabled })}
                checked={bdayEnabled}
                onChange={() => {
                  updateSettings({ bdayEnabled: !bdayEnabled });
                }}
              />
              <Toggle
                label={t('Ссылка на пользовательское соглашение')}
                hintText={
                  <span className={s.agreementHint}>
                    {t('Пользовательское соглашение находится по ссылке:')}
                    <Link
                      to={agreementUrl ? agreementUrl : `/agreement/${company.sid}`}
                      className={s.agreementLink}
                      rel="noopener noreferrer"
                      target="_blank"
                      component="a"
                    >
                      {`https://platform.loyal-up.com/agreement/${company.sid}`}
                    </Link>
                  </span>
                }
                className={cn(s.modelRow, s.enabled, s.blockedCursor)}
                checked={true}
                onChange={() => true}
                disabled
              />

              <Toggle
                label={
                  <div className={s.modelCheckboxArea}>
                    <div className={cn(s.fakedCheckBox, { [s.inactiveFakedCheckBox]: !termsEnabled })}>
                      <CheckedIcon />
                    </div>
                    <span className={cn({ [s.enabled]: termsEnabled })}>
                      {t('Я даю своё согласие на обработку моих персональных данных')}
                    </span>
                  </div>
                }
                className={cn(s.modelRow, { [s.enabled]: termsEnabled })}
                checked={termsEnabled}
                onChange={() => {
                  updateSettings({ termsEnabled: !termsEnabled });
                }}
              />
            </div>
          </main>
        </>
      )}
    </WithTitle>
  );
};

export default ClientSignUpForm;
